/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import axios from 'axios';
import { baseUrl, getToken } from 'Api';
import { uploadBrandLogo } from 'Utils/uploadBrandLogo';
import styles from './index.module.css';

const MAX_LOGO_BYTES = 5 * 1024 * 1024; // 5 MB
const NAME_MAX = 60;

// "Set your brand" — active-membership affiliates set the brand name + logo that
// gets co-branded onto their experience. Logo uploads to S3 (returns a key);
// name + key are persisted via updateManagerConfigs. On success the parent
// refetches affiliateDetails (which returns a fresh presigned logo URL).
const BrandModal = ({
  open,
  onClose,
  initialName = '',
  initialLogoUrl = '',
  existingLogoKey = '',
  logoFolder,
  onSaved,
}) => {
  const [name, setName] = useState(initialName);
  const [file, setFile] = useState(null); // newly picked File (null = keep current)
  const [preview, setPreview] = useState(initialLogoUrl); // shown in the drop area
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(0);
  const [err, setErr] = useState('');
  const fileInputRef = useRef(null);

  // Reset local state whenever the modal (re)opens.
  useEffect(() => {
    if (open) {
      setName(initialName);
      setFile(null);
      setPreview(initialLogoUrl);
      setSaving(false);
      setProgress(0);
      setErr('');
    }
  }, [open, initialName, initialLogoUrl]);

  // Revoke object URLs created for local previews to avoid leaks.
  useEffect(() => () => {
    if (preview && preview.startsWith('blob:')) URL.revokeObjectURL(preview);
  }, [preview]);

  const pickFile = (f) => {
    if (!f) return;
    if (!f.type?.startsWith('image/')) {
      setErr('Please choose an image file.');
      return;
    }
    if (f.size > MAX_LOGO_BYTES) {
      setErr('Logo must be 5 MB or smaller.');
      return;
    }
    setErr('');
    setFile(f);
    setPreview((prev) => {
      if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
      return URL.createObjectURL(f);
    });
  };

  const onInputChange = (e) => pickFile(e.target.files?.[0]);
  const onDrop = (e) => {
    e.preventDefault();
    pickFile(e.dataTransfer?.files?.[0]);
  };

  const canSave = !saving && name.trim().length > 0; // logo optional

  const handleSave = async () => {
    if (!name.trim()) { setErr('Please enter a brand name.'); return; }
    setSaving(true);
    setErr('');
    try {
      // Upload a new logo (→ key) only if the user picked one; otherwise keep
      // the existing stored key.
      let logoKey = existingLogoKey;
      if (file) {
        setProgress(1);
        logoKey = await uploadBrandLogo(file, logoFolder, setProgress);
      }

      const configs = { BrandName: name.trim() };
      if (logoKey) configs.BrandLogo = logoKey;

      const token = getToken();
      const { data } = await axios.post(`${baseUrl}/updateManagerConfigs`, { token, configs });
      if (data?.success === false) {
        throw new Error(data?.message || 'Could not save your brand.');
      }
      onSaved?.();
      onClose?.();
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || 'Could not save your brand. Please try again.');
      setSaving(false);
    }
  };

  const nameChar = name.length;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className={styles.modalOverlay}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => { if (!saving) onClose?.(); }}
        >
          <motion.div
            className={styles.brandModal}
            role="dialog"
            aria-modal="true"
            aria-label="Set your brand"
            initial={{ opacity: 0, scale: 0.94, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.94, y: 12 }}
            transition={{ type: 'spring', stiffness: 320, damping: 30 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.brandModalHead}>
              <div>
                <h3 className={styles.brandModalTitle}>Set your brand</h3>
                <p className={styles.brandModalSub}>
                  Your brand name and logo appear across your affiliate experience.
                </p>
              </div>
              <button
                type="button"
                className={styles.brandModalClose}
                onClick={() => { if (!saving) onClose?.(); }}
                aria-label="Close"
                disabled={saving}
              >
                <CloseRoundedIcon fontSize="small" />
              </button>
            </div>

            <div className={styles.brandModalBody}>
              {/* Logo */}
              <div className={styles.brandField}>
                <span className={styles.brandLabel}>Brand logo</span>
                <div
                  className={styles.brandDrop}
                  onClick={() => !saving && fileInputRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { if (!saving) onDrop(e); }}
                  role="button"
                  tabIndex={0}
                >
                  {preview ? (
                    <img src={preview} alt="Brand logo preview" className={styles.brandPreview} />
                  ) : (
                    <span className={styles.brandDropPlaceholder}>Logo</span>
                  )}
                  <div className={styles.brandDropText}>
                    <span className={styles.brandDropTitle}>
                      {preview ? 'Change logo' : 'Upload a logo'}
                    </span>
                    <span className={styles.brandDropHint}>PNG, JPG or SVG · up to 5 MB</span>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className={styles.brandFileInput}
                    onChange={onInputChange}
                    disabled={saving}
                  />
                </div>
              </div>

              {/* Name */}
              <div className={styles.brandField}>
                <label className={styles.brandLabel} htmlFor="brand-name-input">Brand name</label>
                <input
                  id="brand-name-input"
                  type="text"
                  className={styles.brandInput}
                  placeholder="e.g. Acme Studios"
                  value={name}
                  maxLength={NAME_MAX}
                  onChange={(e) => setName(e.target.value)}
                  disabled={saving}
                />
                <span className={styles.brandCharCount}>{`${nameChar}/${NAME_MAX}`}</span>
              </div>

              {saving && file && (
                <div className={styles.brandProgress}>
                  <span
                    className={styles.brandProgressFill}
                    style={{ width: `${Math.max(4, progress)}%` }}
                  />
                </div>
              )}

              {err && <p className={styles.coError}>{err}</p>}
            </div>

            <div className={styles.brandModalActions}>
              <button
                type="button"
                className={styles.coSecondary}
                onClick={() => { if (!saving) onClose?.(); }}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.gateModalBtn}
                onClick={handleSave}
                disabled={!canSave}
              >
                {saving ? (file ? 'Uploading…' : 'Saving…') : 'Save brand'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BrandModal;
